import { AnimatePresence } from 'framer-motion'
import { Cross as Hamburger } from 'hamburger-react'
import { useEffect, useRef, useState } from 'react'
import { use100vh } from 'react-div-100vh'
import { useTheme } from 'styled-components'
import * as Accordion from '@radix-ui/react-accordion'
import { dataTestIds, promotionalBannerVariants } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { useHeader } from '@velasca/hooks/use-header'
import { useTop } from '@velasca/hooks/use-top'
import { CartIcon } from '@velasca/icons/cart'
import { SearchIcon } from '@velasca/icons/search'
import type { Navbar as NavbarType } from '@velasca/types'
import { IconButton } from '@velasca/ui/icon-button'
import { UnstyledList } from '@velasca/ui/unstyled-list'
import PromotionalBanner from '../PromotionalBanner'
import { AccordionItem } from './AccordionItem/AccordionItem'
import { MenuEntriesWrapper } from './MenuEntriesWrapper/MenuEntriesWrapper'
import {
  Bar,
  CenterChunk,
  CurrencySelector,
  CurrencyTitle,
  EndChunk,
  FirstLevelMenuTitle,
  Header,
  Link,
  LinkNoDecoration,
  LogoContainer,
  LowerMenu,
  LowerMenuItem,
  MenuLists,
  Navbar,
  RightSideContent,
  Select,
  StartChunk,
  StyledBrandLogoExtended,
} from './MobileNavigation.styles'

export const MobileNavigation = ({
  menu,
  lowerMenu,
  promotionalBanner,
  miscHeaderData = {},
  className = '',
  sticky = false,
}: NavbarType) => {
  const [isOpen, setIsOpen] = useState(false)
  const [animationDone, setAnimationDone] = useState(true)
  const promotionalBannerRef = useRef<HTMLDivElement>(null)
  const navbarRef = useRef<HTMLDivElement>(null)
  const top = useTop()
  const viewportHeight = use100vh()
  const theme = useTheme()

  const setPromotionalBannerHeight = useHeader(
    (s) => s.setPromotionalBannerHeight,
  )
  const setNavbarHeight = useHeader((s) => s.setNavbarHeight)

  useEffect(() => {
    const { height = 0 } =
      promotionalBannerRef?.current?.getBoundingClientRect() || {}

    setPromotionalBannerHeight(height)
  }, [setPromotionalBannerHeight])

  useEffect(() => {
    const { height = 0 } = navbarRef?.current?.getBoundingClientRect() || {}

    setNavbarHeight(height)
  }, [setNavbarHeight])

  const headerHeight = useHeader((s) => s.headerHeight)

  useEffect(() => {
    if (sticky) {
      document.body.style.paddingTop = `${headerHeight}px`
    }
  }, [sticky, headerHeight])

  const { cartItemCount, setIsDrawerOpen } = useCart()

  return (
    <Header
      className={className}
      isOpen={isOpen}
      top={top}
      animationDone={animationDone}
      $height={viewportHeight ?? 0}
    >
      <PromotionalBanner
        {...promotionalBanner}
        ref={promotionalBannerRef}
        variant={
          top && !isOpen && !sticky
            ? promotionalBannerVariants.SEMI_TRANSPARENT
            : promotionalBannerVariants.PRIMARY
        }
      />
      <Navbar isOpen={isOpen} top={top} sticky={sticky}>
        <Bar ref={navbarRef} isOpen={isOpen} top={top}>
          <StartChunk>
            <Hamburger
              toggled={isOpen}
              size={24}
              toggle={(e) => {
                if (!e) {
                  setAnimationDone(false)
                }
                setIsOpen(e)
              }}
              color={
                isOpen || !top || sticky
                  ? theme.colors.text.primary
                  : theme.colors.background
              }
            />
          </StartChunk>
          <CenterChunk>
            {miscHeaderData.currentTemplate === 'index' ? (
              <LogoContainer>
                <StyledBrandLogoExtended
                  variant={isOpen || !top || sticky ? 'primary' : 'white'}
                />
              </LogoContainer>
            ) : (
              <LinkNoDecoration href="/" title="Velasca">
                <StyledBrandLogoExtended
                  variant={isOpen || !top || sticky ? 'primary' : 'white'}
                />
              </LinkNoDecoration>
            )}
          </CenterChunk>
          <EndChunk>
            <RightSideContent>
              {miscHeaderData.searchEnabled && (
                <IconButton
                  variant={isOpen || !top || sticky ? 'primary' : 'secondary'}
                >
                  <SearchIcon />
                </IconButton>
              )}
              <IconButton
                data-testid={dataTestIds.CART_ICON}
                variant={isOpen || !top || sticky ? 'primary' : 'secondary'}
                badge={cartItemCount}
                onClick={() => {
                  setIsDrawerOpen(true)
                }}
              >
                <CartIcon data-testid={dataTestIds.CART_ICON} />
              </IconButton>
            </RightSideContent>
          </EndChunk>
        </Bar>
        <AnimatePresence>
          {isOpen && (
            <MenuEntriesWrapper>
              <MenuLists
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onAnimationComplete={() => {
                  setAnimationDone(true)
                }}
              >
                <Accordion.Root asChild type="multiple">
                  <UnstyledList>
                    {menu?.map((firstLevelMenu, firstLevelIndex) => {
                      return (
                        <AccordionItem
                          menu={firstLevelMenu}
                          key={
                            firstLevelMenu.id ??
                            `${firstLevelMenu.handle}-${firstLevelIndex}`
                          }
                        />
                      )
                    })}
                  </UnstyledList>
                </Accordion.Root>
                <LowerMenu>
                  {lowerMenu?.map((thirdLevelMenu) => {
                    return (
                      <LowerMenuItem
                        key={`third-level-${thirdLevelMenu?.title}`}
                      >
                        <Link
                          href={thirdLevelMenu?.url}
                          target={
                            thirdLevelMenu?.isTargetBlank ? '_blank' : '_self'
                          }
                        >
                          <FirstLevelMenuTitle>
                            {thirdLevelMenu?.title}
                          </FirstLevelMenuTitle>
                        </Link>
                      </LowerMenuItem>
                    )
                  })}
                </LowerMenu>
                {miscHeaderData?.currency?.supported && (
                  <CurrencySelector>
                    <CurrencyTitle>Change currency</CurrencyTitle>
                    <Select currencies={miscHeaderData.currency?.supported} />
                  </CurrencySelector>
                )}
              </MenuLists>
            </MenuEntriesWrapper>
          )}
        </AnimatePresence>
      </Navbar>
    </Header>
  )
}
